"use client";

import { Moon, Menu, X } from "lucide-react";
import Link from "next/link";
import React, { useState, useEffect } from "react";
import { smoothScrollTo } from "@/utils/scroll";
import { MotionWrapper } from "./MotionWrapper";

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    smoothScrollTo(id);
    setIsMenuOpen(false);
  };

  const isScrolled = scrollY > 50;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-black/20 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="flex justify-between items-center px-8 py-6">
        <MotionWrapper
          whileHover={{ scale: 1.05 }}
          className="flex items-center cursor-pointer"
        >
          <Moon className="text-[#FFD700] mr-2" size={24} />
          <span className="text-2xl font-bold tracking-wider text-white">
            LUNATY
          </span>
        </MotionWrapper>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8">
          {[
            { href: "#experience", label: "EXPERIENCE" },
            { href: "#profiles", label: "PROFILES" },
            { href: "#clients", label: "CLIENTS" },
          ].map((item, index) => (
            <MotionWrapper
              key={index}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                href={item.href}
                onClick={(e) => handleScroll(e, item.href.slice(1))}
                className="relative group hover:text-[#FFD700] transition-colors duration-300 font-medium"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FFD700] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </MotionWrapper>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden text-white hover:text-[#FFD700] transition-colors duration-300"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <MotionWrapper
        initial={false}
        animate={{
          height: isMenuOpen ? "auto" : 0,
          opacity: isMenuOpen ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        className="md:hidden overflow-hidden bg-black/30 backdrop-blur-md border-t border-gray-800/50"
      >
        <nav className="flex flex-col space-y-4 px-8 py-6">
          {[
            { href: "#experience", label: "EXPERIENCE" },
            { href: "#profiles", label: "PROFILES" },
            { href: "#clients", label: "CLIENTS" },
          ].map((item, index) => (
            <MotionWrapper
              key={index}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                href={item.href}
                onClick={(e) => handleScroll(e, item.href.slice(1))}
                className="text-white hover:text-[#FFD700] transition-colors duration-300 font-medium py-2 border-b border-gray-800/30"
              >
                {item.label}
              </Link>
            </MotionWrapper>
          ))}
        </nav>
      </MotionWrapper>
    </header>
  );
}

export default Header;
